export type AnnuitySeed = { id: string; title: string; principal: number; rate: number; m: number; }
export type BayesSeed = { id: string; title: string; p1: number; p2: number; p3: number; }
export type OptimizationSeed = { id: string; title: string; h: number; L: number; v1: number; v2: number; optX: number; minTime: number; }
export type OxyzSphereSeed = {
  id: string
  center: [number, number, number]
  radius: number
  normal: [number, number, number]
  normalDotCenter: number
  maximum: number
  minimum: number
  tangentPoint: [number, number, number]
}
export type OxyzSectionSeed = {
  id: string
  center: [number, number, number]
  radius: number
  planeZ: number
  sectionRadius: number
  inPlaneDistance: number
}
export type OxyzSkewLinesSeed = {
  id: string
  pointA: [number, number, number]
  directionU: [number, number, number]
  pointB: [number, number, number]
  directionV: [number, number, number]
  cross: [number, number, number]
  alpha: number
  beta: number
  gamma: number
  distanceRadicand: number
  closestPointOnFirst: [number, number, number]
}
export type PharmacokineticsSeed = { id: string; a: number; b: number; }

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = Math.imul(31, hash) + str.charCodeAt(i) | 0;
  return hash;
}

function mulberry32(a: number) {
  return function() {
    let t = a += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
}

export class SeedGenerator {
  private rng: () => number;
  constructor(seedString: string) {
    this.rng = mulberry32(hashString(seedString));
  }
  
  randomInt(min: number, max: number): number {
    return Math.floor(this.rng() * (max - min + 1)) + min;
  }
  
  randomFloat(min: number, max: number): number {
    return this.rng() * (max - min) + min;
  }
}

export function generateAnnuitySeed(id: string): AnnuitySeed {
  const gen = new SeedGenerator(id);
  const contexts = ['Mua chung cư trả góp', 'Vay vốn mở công ty', 'Mua xe ô tô trả góp'];
  const title = contexts[gen.randomInt(0, contexts.length - 1)];
  
  const principal = gen.randomInt(5, 30) * 100; // 500 million to 3 billion
  const rates = [0.5, 0.6, 0.7, 0.8, 0.9, 1.0];
  const rate = rates[gen.randomInt(0, rates.length - 1)]; 
  const minM = Math.floor(principal * (rate / 100)) + 5;
  const m = gen.randomInt(minM, minM + 20); 
  
  return { id, title, principal, rate, m };
}

export function generateBayesSeed(id: string): BayesSeed {
  const gen = new SeedGenerator(id);
  const contexts = ['Xét nghiệm ung thư', 'Sàng lọc virus', 'Kiểm định phế phẩm nhà máy'];
  const title = contexts[gen.randomInt(0, contexts.length - 1)];
  
  // Keep all published data readable without sacrificing seed variety.
  // Raw RNG floats produced distracting 15-digit percentages in the stem.
  const p1 = gen.randomInt(5, 30) / 10; // 0.5% to 3.0% prevalence
  const p2 = gen.randomInt(90, 99);     // whole-percent sensitivity
  const p3 = gen.randomInt(1, 5);       // whole-percent false-positive rate
  
  return { id, title, p1, p2, p3 };
}

export function generateOptimizationSeed(id: string): OptimizationSeed {
  const gen = new SeedGenerator(id);
  const contexts = ['Một người cứu hộ trên biển', 'Cuộc thi ba môn phối hợp', 'Vận chuyển hàng hóa qua sông'];
  const title = contexts[gen.randomInt(0, contexts.length - 1)];
  
  // Pythagorean triples to ensure integer solutions for optimization
  const triples = [
    { v1: 3, k: 4, v2: 5 },
    { v1: 5, k: 12, v2: 13 },
    { v1: 8, k: 15, v2: 17 },
    { v1: 7, k: 24, v2: 25 }
  ];
  
  const t = triples[gen.randomInt(0, triples.length - 1)];
  const multiplier = gen.randomInt(1, 3);
  
  const v1 = t.v1 * multiplier;
  const v2 = t.v2 * multiplier;
  const k = t.k * multiplier;
  
  // x = (v1 * h) / k
  // We want h to be a multiple of k to make x integer
  const h_mult = gen.randomInt(1, 5);
  const h = k * h_mult; // Example: if k=4, h=12. x = 3*12/4 = 9.
  const optX = (v1 * h) / k; 
  
  const L = optX + gen.randomInt(5, 20); // Destination further down the river
  
  const minTime = Number((Math.sqrt(h*h + optX*optX)/v1 + (L - optX)/v2).toFixed(2));
  
  return { id, title, h, L, v1, v2, optX, minTime };
}

/**
 * Advanced Oxyz seeds deliberately use primitive normals of length 3. This keeps
 * the geometry exact while requiring the intended vector / sphere reasoning,
 * rather than decimal calculator work.
 */
export function generateOxyzSphereSeed(id: string): OxyzSphereSeed {
  const gen = new SeedGenerator(id)
  const normals: [number, number, number][] = [[1, 2, 2], [2, -1, 2], [2, 2, -1], [1, -2, 2]]
  const normal = normals[gen.randomInt(0, normals.length - 1)]
  const center: [number, number, number] = [gen.randomInt(-4, 4), gen.randomInt(-4, 4), gen.randomInt(-4, 4)]
  const radius = [3, 6, 9][gen.randomInt(0, 2)]
  const normalDotCenter = center.reduce((sum, value, index) => sum + value * normal[index], 0)
  const displacement = radius / 3
  const tangentPoint: [number, number, number] = [
    center[0] + displacement * normal[0],
    center[1] + displacement * normal[1],
    center[2] + displacement * normal[2],
  ]
  return {
    id,
    center,
    radius,
    normal,
    normalDotCenter,
    maximum: normalDotCenter + 3 * radius,
    minimum: normalDotCenter - 3 * radius,
    tangentPoint,
  }
}

/** Plane sections with Pythagorean radii make the hidden-circle model inspectable. */
export function generateOxyzSectionSeed(id: string): OxyzSectionSeed {
  const gen = new SeedGenerator(id)
  const pairs = [
    { radius: 5, height: 3, sectionRadius: 4 },
    { radius: 10, height: 6, sectionRadius: 8 },
    { radius: 13, height: 5, sectionRadius: 12 },
  ]
  const pair = pairs[gen.randomInt(0, pairs.length - 1)]
  const center: [number, number, number] = [gen.randomInt(-4, 4), gen.randomInt(-4, 4), gen.randomInt(-3, 3)]
  const signedHeight = gen.randomInt(0, 1) === 0 ? pair.height : -pair.height
  return {
    id,
    center,
    radius: pair.radius,
    planeZ: center[2] + signedHeight,
    sectionRadius: pair.sectionRadius,
    inPlaneDistance: pair.sectionRadius + [2, 3, 4][gen.randomInt(0, 2)],
  }
}

/** Skew-line instances are built from an orthogonal decomposition in the basis u, v, u×v. */
export function generateOxyzSkewLinesSeed(id: string): OxyzSkewLinesSeed {
  const gen = new SeedGenerator(id)
  const directionPairs: Array<{ u: [number, number, number]; v: [number, number, number] }> = [
    { u: [1, 1, 0], v: [1, -1, 1] },
    { u: [1, 0, 1], v: [1, 2, -1] },
    { u: [2, 1, 0], v: [1, -1, 2] },
  ]
  const pair = directionPairs[gen.randomInt(0, directionPairs.length - 1)]
  const [u1, u2, u3] = pair.u
  const [v1, v2, v3] = pair.v
  const cross: [number, number, number] = [u2 * v3 - u3 * v2, u3 * v1 - u1 * v3, u1 * v2 - u2 * v1]
  const pointA: [number, number, number] = [gen.randomInt(-3, 3), gen.randomInt(-3, 3), gen.randomInt(-3, 3)]
  const alpha = gen.randomInt(-2, 3)
  const beta = gen.randomInt(-2, 3)
  const gamma = gen.randomInt(1, 2)
  const pointB: [number, number, number] = [
    pointA[0] + alpha * u1 + beta * v1 + gamma * cross[0],
    pointA[1] + alpha * u2 + beta * v2 + gamma * cross[1],
    pointA[2] + alpha * u3 + beta * v3 + gamma * cross[2],
  ]
  const closestPointOnFirst: [number, number, number] = [
    pointA[0] + alpha * u1,
    pointA[1] + alpha * u2,
    pointA[2] + alpha * u3,
  ]
  const crossNormSquared = cross.reduce((sum, value) => sum + value * value, 0)
  return {
    id,
    pointA,
    directionU: pair.u,
    pointB,
    directionV: pair.v,
    cross,
    alpha,
    beta,
    gamma,
    distanceRadicand: gamma * gamma * crossNormSquared,
    closestPointOnFirst,
  }
}

export function generatePharmacokineticsSeed(id: string): PharmacokineticsSeed {
  const gen = new SeedGenerator(id);
  // C(t) = a * t * e^(-b t)
  // We want nice numbers. The max occurs at t = 1/b.
  // We want t to be something like 1, 1.5, 2, 2.5, 3, 4, 5 hours.
  // So b can be 1, 2/3, 1/2, 2/5, 1/3, 1/4, 1/5, etc.
  const bOptions = [1, 0.5, 0.25, 0.2, 0.1];
  const b = bOptions[gen.randomInt(0, bOptions.length - 1)];
  
  // Nồng độ max = (a * 1/b) * e^(-1).
  // e^-1 is ~0.3678.
  // Let a be a multiple of b so that a/b is an integer, making C_max = integer/e.
  const a_mult = gen.randomInt(5, 20); // 5 to 20
  const a = Number((a_mult * b).toFixed(4));
  
  return { id, a, b };
}
